import {
  LockScreen,
  LockScreenMode,
  useIdleLock,
} from "@ews-admin/global-design-system";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { useActivityHeartbeat } from "../../hooks/useActivityHeartbeat";
import {
  clearSession,
  getSessionRequest,
  pinUnlocked,
  requestTerminateSessionRequest,
  sessionLocked,
  validateSessionFailure,
  selectHasPin,
  selectPinLockMinutes,
  selectSessionLocked,
  selectSessionStaticData,
} from "../../store/reducers/sessionSlice";
import { RootState, useAppDispatch } from "../../store/store";
import { SessionStatus } from "../../types/session.definition";
import {
  IDLE_LOCK_STORAGE_KEY,
  lockScreenTexts,
  pinErrorCode,
  pinErrorMessage,
} from "../../utils/pin-lock-utils";
import {
  requestConfirmPinReset,
  requestCreatePin,
  requestPinReset,
  requestUnlockPin,
  PinResetRequestResponse,
} from "../../webclients/pin.client";

/**
 * PIN lock of the private pages (MED-1620). The server refuses the calls of a
 * user without a PIN (403 PIN_REQUIRED) and of a locked session (423
 * SESSION_LOCKED); this gate shows the matching LockScreen:
 *  - no PIN yet → create (blocking);
 *  - locked by the server, by the user's delay without activity (session
 *    `pinLockMinutes`, 15 by default) or by any 423 → unlock.
 * A session without `hasPin` (API without the PIN feature) is left alone.
 */
export const PinLockGate: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const hasPin = useSelector(selectHasPin);
  const serverLocked = useSelector(selectSessionLocked);
  const { sessionStatus } = useSelector(selectSessionStaticData);
  const loading = useSelector((state: RootState) => state.session.loading);
  const session = useSelector((state: RootState) => state.session.session);
  const sessionError = useSelector((state: RootState) => state.session.error);
  const pinLockMinutes = useSelector(selectPinLockMinutes);

  const idle = useIdleLock({
    storageKey: IDLE_LOCK_STORAGE_KEY,
    timeoutMs: pinLockMinutes * 60_000,
    enabled:
      !loading &&
      sessionStatus === SessionStatus.VALID_SESSION &&
      hasPin === true,
  });

  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [reset, setReset] = useState<PinResetRequestResponse | null>(null);

  // A 423 locks every tab, like the inactivity timer.
  const { lock: idleLock, unlock: idleUnlock } = idle;
  useEffect(() => {
    if (hasPin === true && serverLocked) idleLock();
  }, [hasPin, serverLocked, idleLock]);

  // Unlocked in another tab: the server session is open again.
  const wasIdleLocked = useRef(idle.locked);
  useEffect(() => {
    if (wasIdleLocked.current && !idle.locked && serverLocked) {
      dispatch(pinUnlocked());
      dispatch(getSessionRequest());
    }
    wasIdleLocked.current = idle.locked;
  }, [idle.locked, serverLocked, dispatch]);

  let mode: LockScreenMode | null = null;
  if (hasPin === false) mode = "create";
  else if (hasPin === true && (serverLocked || idle.locked))
    mode = reset ? "reset" : "unlock";

  // Keeps the server session open while the user reads or types.
  useActivityHeartbeat(
    mode === null &&
      hasPin === true &&
      !loading &&
      sessionStatus === SessionStatus.VALID_SESSION
  );

  // Pages are only mounted once the session is open: while locked or without
  // a PIN the server refuses their calls and /session holds only the user's
  // id, name and role (no appointments, no metadata).
  // Once shown, they stay mounted under the lock screen (no lost input).
  const [revealed, setRevealed] = useState(mode === null);
  // Opened before the pages were shown: wait for the full session (the
  // licence and role checks of PrivateRoute read it).
  const [reloading, setReloading] = useState(false);
  useEffect(() => {
    setReloading(false);
  }, [session, sessionError]);
  useEffect(() => {
    if (mode === null && !reloading) setRevealed(true);
  }, [mode, reloading]);

  useEffect(() => {
    if (mode === null) {
      setError(null);
      setReset(null);
    }
  }, [mode]);

  const opened = useCallback(() => {
    setError(null);
    setReset(null);
    idleUnlock();
    // Committed before the store update below, which React renders at once.
    if (!revealed) flushSync(() => setReloading(true));
    dispatch(pinUnlocked());
    // /session is partial while locked or without a PIN: reload it in full.
    dispatch(getSessionRequest());
  }, [dispatch, idleUnlock, revealed]);

  // LockScreen expects `error` to be set before the promise rejects.
  const fail = useCallback(
    (cause: unknown) => {
      if (pinErrorCode(cause) === "PIN_ATTEMPTS_EXCEEDED") {
        // Every token is revoked: an invalid session sends PrivateRoute to login.
        idleUnlock();
        dispatch(clearSession());
        dispatch(validateSessionFailure("PIN_ATTEMPTS_EXCEEDED"));
      } else if (
        pinErrorCode(cause) === "PIN_CREATE_REAUTH_REQUIRED" ||
        pinErrorCode(cause) === "SESSION_REUSE_DETECTED"
      ) {
        // The interceptor ended the session: SessionEndedNotice takes over.
      } else if (pinErrorCode(cause) === "PIN_ALREADY_SET") {
        // Created from another tab meanwhile: ask for it instead.
        dispatch(sessionLocked());
      } else {
        setError(pinErrorMessage(t, cause));
      }
      throw cause;
    },
    [dispatch, idleUnlock, t]
  );

  const onCreate = useCallback(
    async (pin: string) => {
      try {
        await requestCreatePin(pin);
        opened();
      } catch (cause) {
        fail(cause);
      }
    },
    [fail, opened]
  );

  const onUnlock = useCallback(
    async (pin: string) => {
      try {
        await requestUnlockPin(pin);
        opened();
      } catch (cause) {
        fail(cause);
      }
    },
    [fail, opened]
  );

  const onReset = useCallback(
    async (otp: string, newPin: string) => {
      try {
        await requestConfirmPinReset(otp, newPin);
        opened();
      } catch (cause) {
        fail(cause);
      }
    },
    [fail, opened]
  );

  const onForgot = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await requestPinReset();
      setReset(data);
    } catch (cause) {
      setError(pinErrorMessage(t, cause));
    } finally {
      setBusy(false);
    }
  }, [t]);

  // Ends the session like the navigation's logout; the lock is lifted first
  // so the next sign-in does not start locked.
  const onSignOut = useCallback(() => {
    idleUnlock();
    dispatch(requestTerminateSessionRequest());
  }, [dispatch, idleUnlock]);

  const onCancelReset = useCallback(() => {
    setError(null);
    setReset(null);
  }, []);

  const otpDescription = reset
    ? t("pinLock.otpSent", {
        channel: t(
          reset.channel === "SMS"
            ? "pinLock.channel.sms"
            : "pinLock.channel.email"
        ),
        destination: reset.destinationMasked,
      })
    : undefined;

  const userName = [session?.user?.firstName, session?.user?.lastName]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      {(revealed || (mode === null && !reloading)) && children}
      {mode && (
        <LockScreen
          // A new screen per mode: a pending answer of the previous mode must
          // not bring its first step back.
          key={mode}
          mode={mode}
          texts={lockScreenTexts(t, otpDescription)}
          error={error}
          busy={busy}
          onCreate={onCreate}
          onUnlock={onUnlock}
          onReset={onReset}
          onForgot={mode === "unlock" ? () => void onForgot() : undefined}
          onCancel={mode === "reset" ? onCancelReset : undefined}
          onSignOut={onSignOut}
          subtitle={
            userName ? t("pinLock.signedInAs", { name: userName }) : undefined
          }
        />
      )}
    </>
  );
};
